import { TbArrowDown, TbChevronRight, TbGripVertical, TbPlus, TbX } from "solid-icons/tb";
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { createStore, produce } from "solid-js/store";
import { onButtonKeyDown } from "../buttons/buttonKeyDown";
import "./live-summary.css";
import { leadingSpeakerLen } from "./accent";
import { effectiveTagIds, orderedTagsFor } from "./effectiveTags";
import { createFollowNew, prefersReducedMotion } from "./followNew";
import { filterMains, groupTopics, kindCounts, matchesFilter, tagCounts } from "./grouping";
import { combineTodoText } from "./merge";
import { NoteRow } from "./NoteRow";
import { rangeIds, toggle } from "./selection";
import { ActiveTagFilter, TagChip } from "./TagChip";
import {
  filterTagId,
  type LiveSummaryFilter,
  type LiveSummaryItemData,
  type LiveSummaryTag,
  type LiveSummaryTopicData,
  tagFilter,
} from "./types";

type KindFilter = "all" | LiveSummaryItemData["kind"];

// Pill styling: to-do amber, decision violet, with All and Context neutral so
// they don't compete. The selected pill is filled. This summary is down to All
// alone; the other branches belong to Chief's timeline, which filters its kind
// pills through this same function so the two rows read as one control.
const filterPillClass = (key: KindFilter, selected: boolean): string => {
  const base =
    "shrink-0 cursor-pointer rounded-full px-2 py-0.5 text-[calc(0.6875rem*var(--font-scale,1))] font-semibold transition-colors";
  if (key === "todo") {
    return `${base} ${selected ? "bg-[#f59e0b]/20 text-[#b45309] dark:text-[#fcd34d]" : "text-[#b45309]/70 hover:bg-[#f59e0b]/10 dark:text-[#fcd34d]/70"}`;
  }
  if (key === "decision") {
    return `${base} ${selected ? "bg-[#8b5cf6]/20 text-[#6d28d9] dark:text-[#c4b5fd]" : "text-[#6d28d9]/70 hover:bg-[#8b5cf6]/10 dark:text-[#c4b5fd]/70"}`;
  }
  return `${base} ${selected ? "bg-secondary-text/15 text-primary-text" : "text-muted-text hover:bg-panel-hover"}`;
};

type LiveSummaryViewProps = {
  headline?: string;
  items: LiveSummaryItemData[];
  // Row click — Chief toggles inline expansion, the web opens its develop flow.
  onItemClick?: (item: LiveSummaryItemData) => void;
  // The currently-expanded item ids and the content to render under each
  // (Chief's inline chat). A list rather than a single id so a consumer can open
  // a whole kind at once. Omit both for a non-expanding, read-only list.
  expandedIds?: readonly string[] | null;
  renderExpanded?: (item: LiveSummaryItemData) => JSX.Element;
  // Optional per-row status accessory (Chief's "Working…" while a chat streams).
  renderStatus?: (item: LiveSummaryItemData) => JSX.Element;
  // Merge one to-do into another: the destination keeps combinedText and the
  // source is treated as removed. When provided, active top-level to-dos show
  // a drag handle and can be dragged onto one another to merge; omit for a
  // read-only view.
  onMerge?: (sourceId: string, destId: string, combinedText: string) => void;
  // Drop / restore. When provided, the active rows show an X and the set-aside
  // rows a +; omit for a read-only view.
  onDrop?: (id: string) => void;
  onRestore?: (id: string) => void;
  onDropMany?: (ids: string[]) => void;
  onRestoreMany?: (ids: string[]) => void;
  // Optional actions for the current modifier-click selection. Supplying this
  // also enables selection for consumers that do not expose drop/restore.
  renderSelectionActions?: (ids: string[], clear: () => void) => JSX.Element;
  // Fade rows in/out as they arrive and move between active and set-aside.
  animate?: boolean;
  // Rendered at the right of the filter toolbar (e.g. an export menu).
  toolbarEnd?: JSX.Element;
  // Rendered inside the scroll container above the headline (e.g. the
  // user-editable session context), so it scrolls with the summary and shows
  // even while the summary is still empty.
  header?: JSX.Element;
  // Rendered when there's nothing to show yet.
  emptyHint?: JSX.Element;
  // Extra classes on the scroll container. Chief passes trailing padding when
  // a long header (the session context) is present, so the user can scroll it
  // out of view even while the summary itself is still short.
  contentClass?: string;
  // Maps a speaker label ("You" / "Them") to the transcript's color for that
  // speaker, used to tint the leading speaker word in each bullet. Omit to leave
  // the summary untinted (the web viewer).
  speakerColor?: (speaker: string | undefined) => string | undefined;
  // Reset the filter back to "all" whenever this changes (e.g. a new session id).
  // The filter signal is component-owned, so a long-lived panel (Chief's dockview
  // summary) would otherwise carry a stale filter into the next session.
  resetKey?: string;
  // The one row a consumer is pointing at from somewhere else (Chief's
  // timeline). Lit until the pointer moves; scrolling it into view is the
  // consumer's, which knows whether its panel is even on screen.
  highlightId?: string;
  // Drive the filter pills from outside, so a consumer can jump the view to a
  // kind (Chief's "handle the to-dos"). Omit `filter` to leave the view
  // uncontrolled — the pills then run off the internal signal exactly as before.
  filter?: LiveSummaryFilter;
  // Drop the whole filter row, kind pills and tag chips alike. For a view already
  // pinned to one kind (a promoted panel) they would only offer to change what
  // the panel is, and a controlled `filter` means they could not do even that.
  hideFilters?: boolean;
  // Opens every topic and set-aside group and holds them open. The find bar sets
  // it while a query is running: a collapsed section has no text on the page, so
  // its matches would be silently absent from the count.
  expandAll?: boolean;
  // The session's tag vocabulary, needed to resolve an item's tagIds to a name
  // and colour. Tags something carries also each get a header pill.
  tags?: readonly LiveSummaryTag[];
};

const isExpanded = (view: LiveSummaryViewProps, id: string): boolean => Boolean(view.expandedIds?.includes(id));

type SelectionController = {
  enabled: () => boolean;
  ids: () => Set<string>;
  handleModifierClick: (e: MouseEvent, itemId: string, group: "active" | "dropped", orderedIds: string[]) => void;
  clear: () => void;
};

type MergeController = {
  sourceId: () => string | null;
  isSource: (id: string) => boolean;
  isTarget: (id: string) => boolean;
  begin: (id: string) => void;
  pickTarget: (destId: string) => void;
  cancel: () => void;
  pendingDest: () => { destId: string; combined: string } | null;
  setPendingCombined: (text: string) => void;
  confirmPending: () => void;
  justMerged: (id: string) => boolean;
};

const ItemRow: Component<{
  item: LiveSummaryItemData;
  dropped: boolean;
  liClass: string;
  onAnimationEnd?: JSX.EventHandler<HTMLLIElement, AnimationEvent>;
  expanded: boolean;
  view: LiveSummaryViewProps;
  onFocusTag?: (tagId: string) => void;
  selection: SelectionController;
  merge: MergeController;
  orderedIds: () => string[];
  // child marks a nested sub-point row (tighter, dimmer). childItems are the
  // active sub-points to render indented beneath a main point.
  child?: boolean;
  childItems?: LiveSummaryItemData[];
}> = (props) => {
  // The leading speaker reference in the bullet text ("You"/"They"/a name), tinted
  // in the transcript's speaker color so the summary reads consistently with the
  // transcript. null when the consumer doesn't color-code (web) or the bullet
  // doesn't open with a speaker.
  const lead = () => {
    const n = leadingSpeakerLen(props.item.text, props.item.owner);
    if (n <= 0) return null;
    const color = props.view.speakerColor?.(props.item.text.slice(0, n).toLowerCase() === "you" ? "You" : "Them");
    return color ? { n, color } : null;
  };
  const isSelected = () => props.selection.enabled() && props.selection.ids().has(props.item.id);
  const itemTags = () => orderedTagsFor(props.item, props.view.tags ?? []);

  const isTopLevelTodo = () => !props.dropped && !props.child && props.item.kind === "todo";
  const mergeActive = () => props.merge.sourceId() !== null;
  const isMergeSource = () => props.merge.isSource(props.item.id);
  const isMergeTargetRow = () =>
    Boolean(props.view.onMerge) && mergeActive() && isTopLevelTodo() && props.merge.isTarget(props.item.id);
  // Stays true for the source once its own drag has begun; without that the grip
  // (the drag handle) would unmount mid-drag and cancel it.
  const canDrag = () => Boolean(props.view.onMerge) && isTopLevelTodo() && (!mergeActive() || isMergeSource());
  const pendingHere = (): { destId: string; combined: string } | null => {
    const pending = props.merge.pendingDest();
    return pending && pending.destId === props.item.id ? pending : null;
  };

  const [draggedOver, setDraggedOver] = createSignal(false);

  return (
    <li
      data-suggestion-id={props.item.id}
      class={props.liClass}
      classList={{ "bg-tile": props.expanded }}
      onAnimationEnd={props.onAnimationEnd}
    >
      <Show when={props.item.kind === "note"}>
        <NoteRow
          item={props.item}
          tags={props.view.tags ?? []}
          onFocusTag={props.onFocusTag}
          onOpen={props.view.onItemClick}
          expandedContent={props.expanded ? props.view.renderExpanded?.(props.item) : undefined}
          onDrop={props.dropped ? undefined : props.view.onDrop}
          onRestore={props.dropped ? props.view.onRestore : undefined}
        />
      </Show>

      <Show when={props.item.kind !== "note"}>
        <div
          role="button"
          tabIndex={0}
          class="group flex cursor-pointer items-start gap-2 rounded-md py-1.5 pr-1.5 pl-2.5 hover:bg-tile"
          classList={{
            // One key owns the tint: classList toggles a key's tokens off whenever
            // that key goes false, so a token shared with another key would be
            // stripped out from under it — which is why the pointed-at row and the
            // merge source share the ring key rather than each declaring one.
            "bg-brand/10": isSelected() || props.view.highlightId === props.item.id || isMergeSource() || draggedOver(),
            "ring-1 ring-inset ring-brand/40": isMergeSource() || props.view.highlightId === props.item.id,
            "ring-2 ring-inset ring-brand": draggedOver(),
            "lsum-merged": props.merge.justMerged(props.item.id),
          }}
          title={isMergeTargetRow() ? "Merge into this to-do" : undefined}
          onDragEnter={(e) => {
            if (!isMergeTargetRow()) return;
            e.preventDefault();
            setDraggedOver(true);
          }}
          onDragOver={(e) => {
            if (!isMergeTargetRow()) return;
            e.preventDefault();
          }}
          onDragLeave={(e) => {
            if (!isMergeTargetRow()) return;
            // dragleave bubbles from child elements; ignore moves that stay
            // within the row so the drop highlight doesn't flicker.
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
            setDraggedOver(false);
          }}
          onDrop={(e) => {
            if (!isMergeTargetRow()) return;
            e.preventDefault();
            setDraggedOver(false);
            props.merge.pickTarget(props.item.id);
          }}
          onClick={(e) => {
            if (!props.selection.enabled()) {
              props.view.onItemClick?.(props.item);
              return;
            }
            if (e.shiftKey || e.metaKey || e.ctrlKey) {
              props.selection.handleModifierClick(
                e,
                props.item.id,
                props.dropped ? "dropped" : "active",
                props.orderedIds()
              );
              return;
            }
            props.selection.clear();
            props.view.onItemClick?.(props.item);
          }}
          onKeyDown={onButtonKeyDown(() => {
            props.selection.clear();
            props.view.onItemClick?.(props.item);
          })}
        >
          <span class="shrink-0 text-[calc(0.8125rem*var(--font-scale,1))] leading-snug" aria-hidden="true">
            <span
              class="inline-block rounded-full bg-secondary-text/60 align-middle transition-colors group-hover:bg-warning"
              classList={{ "size-1": !props.child, "size-[3px] bg-secondary-text/40": props.child }}
            />
          </span>

          <span
            class="min-w-0 flex-1 text-[calc(0.85rem*var(--font-scale,1))] text-primary-text"
            classList={{
              "text-[calc(0.8rem*var(--font-scale,1))] text-secondary-text": props.child,
              "line-through opacity-60": props.item.state === "done",
            }}
          >
            <Show when={isMergeSource()}>
              <span class="relative top-[-1.5px] mr-1.5 inline-flex items-center rounded bg-brand/15 px-1 py-0.5 align-middle text-[0.5625rem] leading-none font-semibold text-brand uppercase">
                Merging
              </span>
            </Show>
            <Show when={lead()} fallback={props.item.text}>
              {(l) => (
                <>
                  <span style={{ color: l().color }}>{props.item.text.slice(0, l().n)}</span>
                  {props.item.text.slice(l().n)}
                </>
              )}
            </Show>

            {/* Under the sentence, indented with it, rather than on a rail at the
                end of the row. A tag is something the point carries, and hanging
                it off the row's right edge read as a property of the list instead
                — the same distance from the sentence whether it described the one
                above or below it. The cost is a line of height per tagged point
                and tags that start wherever the sentence ends; what it buys is
                that a tag and the thing it is about are one block. */}
            <Show when={itemTags().length > 0}>
              <span class="mt-1 flex flex-wrap items-center gap-1">
                <For each={itemTags()}>
                  {(tag) => <TagChip bead tag={tag} onClick={props.onFocusTag && (() => props.onFocusTag?.(tag.id))} />}
                </For>
              </span>
            </Show>
          </span>

          {props.view.renderStatus?.(props.item)}

          {/* Gated on the stable isTopLevelTodo, not canDrag(): merge.begin()
              flips canDrag() false on dragstart, and unmounting the source
              mid-drag can cancel the drag. draggable + the visible grip follow
              canDrag() so nothing drags while a merge is already pending. */}
          <Show when={Boolean(props.view.onMerge) && isTopLevelTodo()}>
            <span
              class="grid size-4.5 shrink-0 place-items-center rounded-full text-muted-text opacity-0 transition-opacity"
              classList={{ "cursor-grab group-hover:opacity-100": canDrag() }}
              title="Drag onto another to-do to merge"
              aria-hidden="true"
              draggable={canDrag()}
              onDragStart={(e) => {
                if (!canDrag()) return;
                e.dataTransfer?.setData("text/plain", props.item.id);
                if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
                props.merge.begin(props.item.id);
              }}
              onDragEnd={() => {
                if (!props.merge.pendingDest()) props.merge.cancel();
                setDraggedOver(false);
              }}
            >
              <Show when={canDrag()}>
                <TbGripVertical size={13} />
              </Show>
            </span>
          </Show>

          <Show when={props.dropped ? props.view.onRestore : props.view.onDrop}>
            <Show
              when={props.dropped}
              fallback={
                <button
                  type="button"
                  title="Set aside"
                  onClick={(e) => {
                    e.stopPropagation();
                    const sel = props.selection.ids();
                    if (sel.size > 0 && sel.has(props.item.id)) {
                      props.view.onDropMany?.(Array.from(sel));
                      props.selection.clear();
                    } else {
                      props.view.onDrop?.(props.item.id);
                    }
                  }}
                  class="grid size-4.5 shrink-0 place-items-center rounded-full text-muted-text opacity-40 transition-opacity group-hover:opacity-100 hover:bg-danger/10 hover:text-danger"
                >
                  <TbX size={12} />
                </button>
              }
            >
              <button
                type="button"
                title="Restore to summary"
                onClick={(e) => {
                  e.stopPropagation();
                  const sel = props.selection.ids();
                  if (sel.size > 0 && sel.has(props.item.id)) {
                    props.view.onRestoreMany?.(Array.from(sel));
                    props.selection.clear();
                  } else {
                    props.view.onRestore?.(props.item.id);
                  }
                }}
                class="grid size-4.5 shrink-0 place-items-center rounded-full text-muted-text opacity-40 transition-opacity group-hover:opacity-100 hover:bg-brand/10 hover:text-brand"
              >
                <TbPlus size={12} />
              </button>
            </Show>
          </Show>
        </div>
      </Show>

      <Show when={pendingHere()}>
        {(pending) => (
          <div
            role="presentation"
            class="mt-1 ml-2.5 flex flex-col gap-1.5 rounded-md border border-brand/40 bg-tile px-2.5 py-2"
            onClick={(e) => e.stopPropagation()}
          >
            <p class="text-[0.75rem] font-medium text-secondary-text">This becomes one to-do</p>
            <textarea
              rows={2}
              maxlength={4000}
              value={pending().combined}
              onInput={(e) => props.merge.setPendingCombined(e.currentTarget.value)}
              class="w-full resize-none rounded-md border border-primary-border bg-panel px-2 py-1 text-[0.85rem] text-primary-text outline-none focus:border-accent"
            />
            <div class="flex justify-end gap-1.5">
              <button
                type="button"
                onClick={() => props.merge.cancel()}
                class="rounded-md px-2 py-1 text-[0.75rem] font-medium text-muted-text hover:bg-panel-hover"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!pending().combined.trim()}
                onClick={() => props.merge.confirmPending()}
                class="rounded-md bg-brand px-2 py-1 text-[0.75rem] font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                Merge
              </button>
            </div>
          </div>
        )}
      </Show>

      {/* A note carries its own chat inside its card; everything else is a
          bullet with the chat under it. */}
      <Show when={props.item.kind !== "note" && props.expanded && props.view.renderExpanded}>
        {props.view.renderExpanded?.(props.item)}
      </Show>

      <Show when={props.childItems && props.childItems.length > 0}>
        <ul class="mt-0.5 ml-3 flex flex-col gap-0.5 border-l border-primary-border/40 pl-1.5">
          <For each={props.childItems}>
            {(ch) => (
              <ItemRow
                item={ch}
                dropped={false}
                child
                liClass="lsum-row rounded-md"
                expanded={isExpanded(props.view, ch.id)}
                view={props.view}
                onFocusTag={props.onFocusTag}
                selection={props.selection}
                merge={props.merge}
                orderedIds={props.orderedIds}
              />
            )}
          </For>
        </ul>
      </Show>
    </li>
  );
};

type FadeRow = { id: string; item: LiveSummaryItemData; leaving: boolean; enter: boolean };

// FadeList animates rows only on genuine data changes (arrive/leave), and swaps
// instantly when the filter changes (a view change, not a data change — animating
// it would crossfade the whole old set against the new one). With animate=false
// it mirrors the items exactly with no animation.
const FadeList: Component<{
  items: LiveSummaryItemData[];
  filter: LiveSummaryFilter;
  animate: boolean;
  children: (row: FadeRow, remove: (id: string) => void, orderedIds: () => string[]) => JSX.Element;
}> = (props) => {
  const [rows, setRows] = createStore<FadeRow[]>(
    props.items.map((it) => ({ id: it.id, item: it, leaving: false, enter: false }))
  );
  let prevFilter = props.filter;

  createEffect(() => {
    const next = props.items;
    const filterChanged = props.filter !== prevFilter;
    prevFilter = props.filter;
    const ids = new Set(next.map((it) => it.id));
    setRows(
      produce((list) => {
        // A leaving row is held on screen until its fade-out fires `animationend`,
        // and live-summary.css drops that animation under prefers-reduced-motion —
        // so the event never arrives and the row never leaves. The preference is
        // honoured here instead, on the half that owns the removal.
        if (!props.animate || filterChanged || prefersReducedMotion()) {
          const byId = new Map(list.map((r) => [r.id, r]));
          list.length = 0;
          for (const it of next) {
            const existing = byId.get(it.id);
            if (existing) {
              existing.item = it;
              existing.leaving = false;
              existing.enter = false;
              list.push(existing);
            } else {
              list.push({ id: it.id, item: it, leaving: false, enter: false });
            }
          }
          return;
        }
        for (const r of list) {
          r.leaving = !ids.has(r.id);
          const fresh = next.find((it) => it.id === r.id);
          if (fresh) r.item = fresh;
        }
        const present = new Set(list.map((r) => r.id));
        for (const it of next) {
          if (!present.has(it.id)) list.push({ id: it.id, item: it, leaving: false, enter: true });
        }
      })
    );
  });

  const remove = (id: string): void => setRows((l) => l.filter((r) => r.id !== id));

  // The shift-range anchor resolves against the order rows are actually rendered
  // in, which is this model — not the source array. FadeList appends streaming
  // items out of source order, so a source-order range would skip the rows
  // between the two clicked endpoints.
  const orderedIds = (): string[] => rows.map((r) => r.id);

  return <For each={rows}>{(row) => props.children(row, remove, orderedIds)}</For>;
};

const TopicSection: Component<{
  title: string;
  topic: () => LiveSummaryTopicData | undefined;
  filter: LiveSummaryFilter;
  view: LiveSummaryViewProps;
  onFocusTag?: (tagId: string) => void;
  selection: SelectionController;
  merge: MergeController;
}> = (props) => {
  // Collapse is purely visual and not persisted.
  const [collapsed, setCollapsed] = createSignal(false);
  const [showDropped, setShowDropped] = createSignal(false);
  // The reader's own collapse state is left alone underneath, so it comes back
  // when the search that forced everything open ends.
  const isCollapsed = (): boolean => collapsed() && !props.view.expandAll;
  const droppedShown = (): boolean => showDropped() || Boolean(props.view.expandAll);
  const mains = () => props.topic()?.items ?? [];
  const childrenFor = (id: string): LiveSummaryItemData[] =>
    (mains().find((m) => m.item.id === id)?.children ?? []).filter((c) => matchesFilter(c, props.filter));
  const activeItems = (): LiveSummaryItemData[] => filterMains(mains(), props.filter);
  const dropped = () => (props.topic()?.dropped ?? []).filter((it) => matchesFilter(it, props.filter));

  const renderRow = (isDropped: boolean) => {
    return (row: FadeRow, remove: (id: string) => void, orderedIds: () => string[]) => (
      <ItemRow
        item={row.item}
        dropped={isDropped}
        liClass={`lsum-row rounded-md${row.enter && !row.leaving ? " lsum-enter" : ""}${row.leaving ? " lsum-leaving" : ""}`}
        onAnimationEnd={(e) => {
          if (row.leaving && e.target === e.currentTarget) remove(row.id);
        }}
        expanded={isExpanded(props.view, row.id)}
        view={props.view}
        onFocusTag={props.onFocusTag}
        selection={props.selection}
        merge={props.merge}
        orderedIds={orderedIds}
        childItems={isDropped ? undefined : childrenFor(row.item.id)}
      />
    );
  };

  return (
    <Show when={activeItems().length > 0 || dropped().length > 0}>
      <div class="mb-3">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          // A running query holds every section open, so a live click would do
          // nothing visible now and collapse the section by surprise later.
          disabled={Boolean(props.view.expandAll)}
          class="group mb-1 flex w-full items-center gap-1 text-base font-semibold text-primary-text"
          classList={{ "cursor-pointer": !props.view.expandAll }}
        >
          <TbChevronRight
            size={14}
            class="shrink-0 text-muted-text transition group-hover:text-warning"
            classList={{ "rotate-90": !isCollapsed() }}
          />
          {props.title}
        </button>

        <Show when={!isCollapsed()}>
          <ul class="flex flex-col gap-0.5 pl-1.5">
            <FadeList items={activeItems()} filter={props.filter} animate={Boolean(props.view.animate)}>
              {renderRow(false)}
            </FadeList>
          </ul>

          <Show when={dropped().length > 0}>
            <button
              type="button"
              onClick={() => setShowDropped((v) => !v)}
              disabled={Boolean(props.view.expandAll)}
              class="mt-0.5 flex w-full items-center gap-1 pl-1.5 text-[calc(0.6875rem*var(--font-scale,1))] font-medium text-muted-text/70 transition-colors hover:text-warning"
            >
              <TbChevronRight size={11} class="shrink-0 transition" classList={{ "rotate-90": droppedShown() }} />
              {dropped().length} set aside
            </button>
            <Show when={droppedShown()}>
              <ul class="mt-0.5 flex flex-col gap-0.5 pl-1.5 opacity-60">
                <FadeList items={dropped()} filter={props.filter} animate={Boolean(props.view.animate)}>
                  {renderRow(true)}
                </FadeList>
              </ul>
            </Show>
          </Show>
        </Show>
      </div>
    </Show>
  );
};

/**
 * Presentational live-meeting summary: an Overview paragraph, filter pills, and
 * topic sections with collapsible groups and a per-topic "set aside" sub-section.
 * All interaction is supplied by the consumer through optional callbacks/slots,
 * so the same view drives Chief (interactive: drop/restore + inline chat) and the
 * web (read-only + develop flow).
 */
export const LiveSummaryView: Component<LiveSummaryViewProps> = (props) => {
  // The internal signal keeps running even when controlled, so a consumer that
  // supplies `filter` but drops it later resumes from what the pills last showed.
  const [ownFilter, setOwnFilter] = createSignal<LiveSummaryFilter>("all");
  const filter = (): LiveSummaryFilter => props.filter ?? ownFilter();
  const setFilter = (next: LiveSummaryFilter): void => {
    setOwnFilter(next);
  };
  createEffect(
    on(
      () => props.resetKey,
      () => setFilter("all"),
      { defer: true }
    )
  );
  const headline = () => props.headline?.trim() ?? "";
  // The vocabulary exactly as the consumer hands it over. A consumer with none
  // to hand over — the public shared-session page, when its API sends none —
  // passes BUILT_IN_DEFAULT_TAGS itself, deliberately and visibly, rather than this
  // substituting them whenever the list arrives empty: an empty list is far more
  // often a vocabulary that hasn't loaded yet, and standing in for it swaps every
  // tag's identity. The built-in ids match nothing the session stores, so the
  // substitution used to resurrect a default somebody had taken off by hand and
  // drop every Smart Tag a pass had applied — while the tag manager, reading the
  // same empty list, said there were no tags at all.
  const tags = (): readonly LiveSummaryTag[] => props.tags ?? [];
  // Resolve each item's tags once, here, so the sections, the filter pills and
  // every row read the same merged set instead of each merging it again.
  const items = createMemo<LiveSummaryItemData[]>(() =>
    props.items.map((it) => ({ ...it, tagIds: effectiveTagIds(it, tags()) }))
  );
  const hasContent = () => headline().length > 0 || props.items.length > 0;
  // What the current filter actually puts on the page. Every section renders off
  // the filtered list, so a view holding items of other kinds only looks
  // non-empty: without this the empty hint is skipped and the panel is blank
  // with nothing to say why. Deliberately not what `hasToolbar` reads — the
  // toolbar has to survive its own filter emptying out, or there is no pill left
  // to get back to All.
  //
  // Reads items() rather than props.items: a Smart Tag's applications live in
  // autoTagIds, which only the memo above folds into tagIds. Against the raw
  // list a tag filter matches nothing, and the panel shows the empty hint while
  // the pill beside it still counts what it can't see.
  const hasVisibleContent = () =>
    (filter() === "all" && headline().length > 0) || items().some((it) => matchesFilter(it, filter()));
  // A consumer's own control has to stay reachable while the summary is still
  // empty — Chief hangs the switch to its other view there, and an empty summary
  // is exactly when someone reaches for it.
  // Key sections by title (a stable string) so a section — and its collapse +
  // fade state — survives each data change instead of remounting.
  const topics = createMemo(() => groupTopics(items()));
  const titles = createMemo(() => topics().map((t) => t.title));
  const topicByTitle = (title: string): LiveSummaryTopicData | undefined => topics().find((t) => t.title === title);
  const counts = createMemo(() => kindCounts(items()));
  // Only tags something carries get a pill, and the pill disappears again when
  // the last item carrying it goes — the rule the kind pills already follow,
  // including their exception for the pill currently filtered on. Without it,
  // setting aside the last item carrying the active tag takes its pill away and
  // leaves an empty list with nothing on screen to say why or to unset.
  const usedTags = (): { tag: LiveSummaryTag; count: number }[] => {
    const counted = tagCounts(items(), tags());
    const activeID = filterTagId(filter());
    if (activeID === null || counted.some((t) => t.tag.id === activeID)) return counted;
    const tag = tags().find((t) => t.id === activeID);
    return tag ? [...counted, { tag, count: 0 }] : counted;
  };

  // One gesture wherever a tag appears: filter this summary to it, and click it
  // again to get back.
  const focusTag = (tagId: string): void => {
    setFilter(filter() === tagFilter(tagId) ? "all" : tagFilter(tagId));
  };

  const focusedTag = (): { tag: LiveSummaryTag; count: number } | undefined => {
    const id = filterTagId(filter());
    return id === null ? undefined : usedTags().find((t) => t.tag.id === id);
  };

  const selectionEnabled = () => Boolean(props.onDrop || props.onRestore || props.renderSelectionActions);

  const [selectedIds, setSelectedIds] = createSignal<Set<string>>(new Set());
  const [anchorId, setAnchorId] = createSignal<string | null>(null);
  const [selectionGroup, setSelectionGroup] = createSignal<"active" | "dropped" | null>(null);

  // Without the filter row the bar would be an empty ruled strip, so a view that
  // hides it only earns the space when something else is in it. A live selection
  // counts: the bar is the only place the selection actions and the count that
  // clears them are drawn, so a view that suppresses it there can be selected
  // into with no way out.
  const hasToolbar = () =>
    (hasContent() && !props.hideFilters) || Boolean(props.toolbarEnd) || (selectionEnabled() && selectedIds().size > 0);

  const clearSelection = (): void => {
    setSelectedIds(new Set<string>());
    setAnchorId(null);
    setSelectionGroup(null);
  };

  const [mergeSourceId, setMergeSourceId] = createSignal<string | null>(null);
  const [pendingDest, setPendingDest] = createSignal<{ destId: string; combined: string } | null>(null);
  const [justMergedId, setJustMergedId] = createSignal<string | null>(null);
  const mergeSourceItem = createMemo(() => items().find((it) => it.id === mergeSourceId()));

  let pulseTimer: ReturnType<typeof setTimeout> | undefined;
  const pulseDestination = (destId: string): void => {
    clearTimeout(pulseTimer);
    setJustMergedId(destId);
    pulseTimer = setTimeout(() => setJustMergedId(null), 1200);
  };
  onCleanup(() => clearTimeout(pulseTimer));

  const cancelMerge = (): void => {
    setMergeSourceId(null);
    setPendingDest(null);
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      clearSelection();
      cancelMerge();
    }
  };
  onMount(() => {
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  const merge: MergeController = {
    sourceId: mergeSourceId,
    isSource: (id) => mergeSourceId() === id,
    isTarget: (id) => mergeSourceId() !== null && mergeSourceId() !== id,
    begin(id: string): void {
      clearSelection();
      setPendingDest(null);
      setMergeSourceId(id);
    },
    pickTarget(destId: string): void {
      const sourceText = mergeSourceItem()?.text ?? "";
      const destText = items().find((it) => it.id === destId)?.text ?? "";
      setPendingDest({ destId, combined: combineTodoText(destText, sourceText) });
    },
    cancel: cancelMerge,
    pendingDest,
    setPendingCombined(text: string): void {
      setPendingDest((prev) => (prev ? { ...prev, combined: text } : prev));
    },
    confirmPending(): void {
      const src = mergeSourceId();
      const pending = pendingDest();
      if (!src || !pending) return;
      const trimmed = pending.combined.trim();
      if (!trimmed) return;
      props.onMerge?.(src, pending.destId, trimmed);
      pulseDestination(pending.destId);
      cancelMerge();
    },
    justMerged: (id) => justMergedId() === id,
  };

  const selection: SelectionController = {
    enabled: selectionEnabled,
    ids: selectedIds,
    handleModifierClick(e: MouseEvent, itemId: string, group: "active" | "dropped", orderedIds: string[]): void {
      if (e.shiftKey) {
        e.preventDefault();
        if (selectionGroup() !== null && selectionGroup() !== group) {
          setSelectedIds(new Set([itemId]));
          setAnchorId(itemId);
          setSelectionGroup(group);
        } else {
          const currentAnchor = anchorId();
          const anchorFound = currentAnchor !== null && orderedIds.includes(currentAnchor);
          const range = rangeIds(orderedIds, currentAnchor, itemId);
          const next = new Set(selectedIds());
          for (const id of range) next.add(id);
          setSelectedIds(next);
          if (selectionGroup() === null) setSelectionGroup(group);
          if (!anchorFound) setAnchorId(itemId);
        }
        return;
      }
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        if (selectionGroup() !== null && selectionGroup() !== group) setSelectedIds(new Set([itemId]));
        else setSelectedIds(toggle(selectedIds(), itemId));
        setAnchorId(itemId);
        setSelectionGroup(group);
      }
    },
    clear: clearSelection,
  };

  // Follow insights as they stream in: a reader watching the newest is carried
  // along, while one who has scrolled up to read is left in place and offered a
  // pill to jump to what arrived (ENG-7177). Positions read from a row's rect,
  // never the container's scrollHeight: a consumer can pad the scroll box
  // (Chief pads it so the brief scrolls away), and that padding is not an insight.
  let scrollRef: HTMLDivElement | undefined;
  const follow = createFollowNew({
    edge: "bottom",
    container: () => scrollRef,
    rowAttr: "data-suggestion-id",
    ids: () => props.items.map((it) => it.id),
    animate: () => Boolean(props.animate),
    resetKey: () => props.resetKey ?? "",
  });
  // A filter change swaps the list under a fixed scroll position without firing a
  // scroll, so re-derive the pin from where the reader now sits — otherwise a pin
  // set on a short filtered list carries onto a long one and the next insight
  // scrolls them off what they were reading.
  createEffect(on(filter, () => follow.recomputePin(), { defer: true }));

  return (
    <div class="relative flex h-full flex-col">
      <Show when={hasToolbar()}>
        <div class="flex shrink-0 items-center justify-between gap-2 border-b border-primary-border px-2 py-1">
          {/* scrollbar-hide, not a [&::-webkit-scrollbar] utility: the theme sets
              the scrollbar globally and unlayered, which beats anything Tailwind
              emits into @layer utilities. Left visible, the bar grows inside this
              scroller and pushes the controls on the row's right below the pills.
              The strip still scrolls; a pill clipped mid-chip is what says so. */}
          {/* Filtered to a tag, the row is that tag: the whole vocabulary plus a
              second bar repeating the one you picked was the same thing three
              times. A kind filter does not collapse — All is how you get back
              from one the timeline set. */}
          <Show
            when={focusedTag()}
            fallback={
              <div class="scrollbar-hide flex min-w-0 items-center gap-1 overflow-x-auto">
                <Show when={!props.hideFilters}>
                  <button
                    type="button"
                    onClick={() => setFilter("all")}
                    class={filterPillClass("all", filter() === "all")}
                  >
                    All <span class="opacity-60">{counts().all}</span>
                  </button>
                  <Show when={usedTags().length > 0}>
                    <span class="mx-0.5 min-h-4.5 w-px shrink-0 self-stretch bg-primary-border" aria-hidden="true" />
                    <For each={usedTags()}>
                      {(t) => (
                        <TagChip
                          tag={t.tag}
                          count={t.count}
                          title={`Filter to ${t.tag.name} (${t.count})`}
                          onClick={() => focusTag(t.tag.id)}
                        />
                      )}
                    </For>
                  </Show>
                </Show>
              </div>
            }
          >
            {(f) => <ActiveTagFilter tag={f().tag} onClear={() => setFilter("all")} />}
          </Show>
          <Show when={selectionEnabled() && selectedIds().size > 0}>
            <div class="flex shrink-0 items-center gap-2">
              {props.renderSelectionActions?.([...selectedIds()], clearSelection)}
              <button
                type="button"
                onClick={clearSelection}
                class="text-[calc(0.6875rem*var(--font-scale,1))] font-medium text-muted-text hover:text-primary-text"
              >
                {selectedIds().size} selected · Clear
              </button>
            </div>
          </Show>
          {props.toolbarEnd}
        </div>
      </Show>

      {/* overscroll-contain keeps a wheel that runs off the end of the list from
          chaining into whatever the consumer mounted this in — enough of that and
          the toolbar rides out of view, taking the filters with it. The class is
          how a consumer finds this element to scroll a row into view itself. */}
      <div
        ref={scrollRef}
        onScroll={follow.onScroll}
        class={`min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 pl-6 ${props.contentClass ?? ""}`}
      >
        {props.header}
        <Show when={hasVisibleContent()} fallback={props.emptyHint}>
          <Show when={filter() === "all" && headline()}>
            <div class="mb-3">
              <h3 class="mb-1 text-base font-semibold text-primary-text">Overview</h3>
              <p class="text-[calc(0.85rem*var(--font-scale,1))] leading-relaxed text-secondary-text">{headline()}</p>
            </div>
          </Show>
          <For each={titles()}>
            {(title) => (
              <TopicSection
                title={title}
                topic={() => topicByTitle(title)}
                filter={filter()}
                view={props}
                onFocusTag={focusTag}
                selection={selection}
                merge={merge}
              />
            )}
          </For>
        </Show>
      </div>

      <Show when={props.animate && follow.showNew()}>
        <button
          type="button"
          onClick={follow.jumpToNewest}
          class="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-[calc(0.75rem*var(--font-scale,1))] font-semibold text-white shadow-lg transition-colors hover:bg-brand-hover"
        >
          <TbArrowDown size={14} aria-hidden />
          New insights
        </button>
      </Show>
    </div>
  );
};
